import { useEffect, useState } from "react";
import type { Movie } from "@/mocks/movies";

type ShareDialogProps = {
  movie: Movie;
  open: boolean;
  onClose: () => void;
};

export default function ShareDialog({ movie, open, onClose }: ShareDialogProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCopied(false);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const link = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `${movie.title} 高清在线观看 - 蜜桃成熟时在线观看`;

  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const input = document.createElement("textarea");
        input.value = link;
        input.setAttribute("readonly", "readonly");
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/70 px-4 py-8 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="分享影片"
      onClick={onClose}
    >
      <div
        className="animate-fade-up w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-label text-[11px] uppercase tracking-[0.2em] text-primary-600">Share</p>
            <h2 className="mt-1 text-lg font-semibold text-foreground-950">分享《{movie.title}》</h2>
            <p className="mt-1 text-xs text-foreground-500">复制链接发给朋友，或者分享到社交平台。</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭分享弹窗"
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-900"
          >
            <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center" />
          </button>
        </div>

        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 p-1.5 pl-3">
          <span className="min-w-0 flex-1 truncate font-label text-xs text-foreground-600">{link}</span>
          <button
            type="button"
            onClick={copyLink}
            className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium transition-colors ${
              copied
                ? "bg-primary-600 text-background-50"
                : "bg-primary-500 text-background-50 hover:bg-primary-600"
            }`}
          >
            <i className={`${copied ? "ri-check-line" : "ri-file-copy-line"} text-sm w-4 h-4 flex items-center justify-center`} />
            {copied ? "已复制" : "复制链接"}
          </button>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <a
            href={`https://service.weibo.com/share/share.php?url=${encodeURIComponent(link)}&title=${encodeURIComponent(shareText)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-2 rounded-md border border-background-200 bg-background-50 py-3 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-weibo-fill text-lg w-[18px] h-[18px] flex items-center justify-center text-primary-500" />
            微博
          </a>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(link)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-2 rounded-md border border-background-200 bg-background-50 py-3 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-twitter-x-fill text-lg w-[18px] h-[18px] flex items-center justify-center text-foreground-800" />
            X
          </a>
          <button
            type="button"
            onClick={copyLink}
            className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-background-200 bg-background-50 py-3 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-link text-lg w-[18px] h-[18px] flex items-center justify-center text-accent-600" />
            链接
          </button>
        </div>

        <p className="mt-4 text-[11px] leading-relaxed text-foreground-400">
          提示：分享链接仅指向本站详情页，站内不提供真实片源。
        </p>
      </div>
    </div>
  );
}